import { Hud, OrthographicCamera } from "@react-three/drei";
import { useEffect, useMemo } from "react";

import type { MatchHudLayout } from "../alpha/hud/hud-layout";
import { screenRectCentre } from "../alpha/hud/hud-layout";
import type { MatchHudModel } from "../alpha/hud/match-hud-model";
import { hudTextTextureCache, type HudTextureKind } from "./HudTextTextureCache";

interface HudPanelProps {
  readonly kind: HudTextureKind;
  readonly rectangle: MatchHudLayout["topConsole"];
  readonly layout: MatchHudLayout;
  readonly model: MatchHudModel;
  readonly label?: string | undefined;
  readonly enabled?: boolean | undefined;
  readonly renderOrder: number;
}

function HudPanel({ kind, rectangle, layout, model, label, enabled, renderOrder }: HudPanelProps) {
  const texture = useMemo(
    () =>
      hudTextTextureCache.get({
        kind,
        width: rectangle.width,
        height: rectangle.height,
        model,
        label,
        enabled,
      }),
    [enabled, kind, label, model, rectangle.height, rectangle.width],
  );
  const [screenX, screenY] = screenRectCentre(rectangle);
  return (
    <mesh
      position={[
        screenX - layout.viewportWidth / 2,
        layout.viewportHeight / 2 - screenY,
        renderOrder,
      ]}
      renderOrder={renderOrder}
      userData={{ objectKind: "screen-hud", hudKind: kind }}
    >
      <planeGeometry args={[rectangle.width, rectangle.height]} />
      <meshBasicMaterial
        map={texture}
        transparent
        depthTest={false}
        depthWrite={false}
        toneMapped={false}
      />
    </mesh>
  );
}

interface TestHudSceneApi {
  readonly snapshot: () => {
    readonly layout: MatchHudLayout;
    readonly textureCount: number;
    readonly visibleOwner: "r3f";
  };
}

declare global {
  interface Window {
    __FATE_HUD_SCENE__?: TestHudSceneApi;
  }
}

export function GameHud3D({
  layout,
  model,
}: {
  readonly layout: MatchHudLayout;
  readonly model: MatchHudModel;
}) {
  useEffect(() => {
    if (import.meta.env.MODE !== "test") return;
    const api: TestHudSceneApi = {
      snapshot: () => ({
        layout,
        textureCount: hudTextTextureCache.size(),
        visibleOwner: "r3f",
      }),
    };
    window.__FATE_HUD_SCENE__ = api;
    return () => {
      if (window.__FATE_HUD_SCENE__ === api) delete window.__FATE_HUD_SCENE__;
    };
  }, [layout]);

  return (
    <Hud renderPriority={1}>
      <OrthographicCamera
        makeDefault
        left={-layout.viewportWidth / 2}
        right={layout.viewportWidth / 2}
        top={layout.viewportHeight / 2}
        bottom={-layout.viewportHeight / 2}
        near={0.1}
        far={100}
        position={[0, 0, 50]}
      />
      <HudPanel
        kind="top-console"
        rectangle={layout.topConsole}
        layout={layout}
        model={model}
        renderOrder={10}
      />
      <HudPanel
        kind="menu"
        rectangle={layout.menu}
        layout={layout}
        model={model}
        label="☰"
        renderOrder={14}
      />
      {model.aiState === "hidden" ? null : (
        <>
          <HudPanel
            kind="ai-button"
            rectangle={layout.pauseAi}
            layout={layout}
            model={model}
            label={model.aiState === "paused" ? "▶" : "Ⅱ"}
            renderOrder={14}
          />
          <HudPanel
            kind="ai-button"
            rectangle={layout.fastAi}
            layout={layout}
            model={model}
            label="≫"
            enabled={model.aiState !== "fast" && model.aiState !== "paused"}
            renderOrder={14}
          />
        </>
      )}
      <HudPanel
        kind="status"
        rectangle={layout.statusMessage}
        layout={layout}
        model={model}
        renderOrder={12}
      />
      {model.phaseActionLabel === null ? null : (
        <HudPanel
          kind="phase-action"
          rectangle={layout.phaseAction}
          layout={layout}
          model={model}
          label={model.phaseActionLabel}
          enabled={model.phaseActionEnabled}
          renderOrder={16}
        />
      )}
    </Hud>
  );
}
